import React from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { pickDisplayPrice } from "../../utils/pricePicker";
import { useCurrency } from "../../components/PriceFormat/PriceFormat";

const AddtoCart = ({ product, selectedColor }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useCurrency();

  const norm = (v) => (v ?? "").toString().trim().toLowerCase();

  const variations = product?.color_images || [];
  const defaultImages = product?.default_images || [];

  // ✅ Dil değişse bile stabil: ürün + varyasyon adı (image değil!)
  const makeVariantKey = (p, variationName) => {
    const base = p.stock_code || p.id;
    const v = norm(variationName) || "default";
    return `${base}|${v}`;
  };

  // selectedColor (Black/Siyah/أسود/...) -> doğru varyasyonu bul
  const resolveVariation = (sel) => {
    const s = norm(sel);
    if (!s || s === "default") return null;

    // 1) Direkt isim eşleşmesi
    let v = variations.find((x) => norm(x.name) === s);
    if (v) return v;

    // 2) Çeviri eşleşmesi
    const langs = Array.from(
      new Set([i18n.language, ...(i18n.languages || []), "en", "tr", "ar"])
    );
    v = variations.find((x) =>
      langs.some((lng) => {
        const a = norm(t(x.name, { lng, defaultValue: "" }));
        const b = norm(t(`colors.${x.name}`, { lng, defaultValue: "" }));
        return (a && a === s) || (b && b === s);
      })
    );
    if (v) return v;

    // 3) Eğer sel URL ise image listesinde ara (opsiyonel)
    const looksUrl = s.startsWith("http://") || s.startsWith("https://");
    if (looksUrl) {
      v = variations.find((x) =>
        (x.images || []).some((img) => norm(img) === s)
      );
      if (v) return v;
    }

    return null;
  };

  const addToCart = () => {
    const found = resolveVariation(selectedColor);

    // ✅ Sepete kaydedilecek varyasyon (name gerçek varyasyon adı olmalı)
    const finalVariation = found
      ? { name: found.name, images: found.images || [] }
      : { name: "Default", images: defaultImages };

    // ✅ ARTIK images'a göre değil, varyasyon adına göre key
    const variantKey = makeVariantKey(product, finalVariation.name);

    const cart = JSON.parse(localStorage.getItem("cart")) || [];

    const existingProductIndex = cart.findIndex((item) => {
      const itemVarName = item.selectedVariation?.name || "Default";
      const itemKey = item.variantKey || makeVariantKey(item, itemVarName);
      return itemKey === variantKey;
    });

    if (existingProductIndex > -1) {
      if ((cart[existingProductIndex].quantity || 1) < 3) {
        cart[existingProductIndex].quantity += 1;

        // ✅ fiyat kaynağını güncel tut
        if (found?.displayPrices) {
          cart[existingProductIndex].displayPrices = found.displayPrices;
        }
        cart[existingProductIndex].priceSnapshot = pickDisplayPrice(
          product,
          currency,
          found?.name || null
        );
      } else {
        Swal.fire({
          icon: "warning",
          title: t("cart.limitReachedTitle"),
          text: t("cart.limitReachedText"),
          confirmButtonText: t("OK"),
        });
        return;
      }
    } else {
      cart.push({
        ...product,
        selectedVariation: finalVariation,
        variantKey,
        quantity: 1,

        // ✅ varyasyonun displayPrices'ı (Cart'ta doğru hesap için)
        displayPrices: found?.displayPrices || product?.displayPrices || null,

        // ✅ opsiyonel snapshot
        priceSnapshot: pickDisplayPrice(product, currency, found?.name || null),
      });
    }

    localStorage.setItem("cart", JSON.stringify(cart));
    window.dispatchEvent(new Event("cartUpdated"));

    Swal.fire({
      icon: "success",
      title: t("cart.addedTitle", "Product added to cart"),
      toast: true,
      position: "bottom-end",
      showConfirmButton: false,
      timer: 1000,
      timerProgressBar: true,
    }).then(() => navigate("/cart"));
  };

  return (
    <button
      className="btn fw-14 bgcolorfooterbottom w-100 p-2 fw-normal mb-1"
      onClick={addToCart}
    >
      <i className="fa-solid fa-cart-plus fa-1x me-2"></i> {t("Add to Cart")}
    </button>
  );
};

export default AddtoCart;
